import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  FolderOpen, Upload, Loader2, Search, Tag, Eye, EyeOff, Trash2,
  Image as ImageIcon, ArrowLeft, RefreshCw, Link2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const FOLDER_LABELS = {
  services: "Services",
  inspo_from_clients: "Inspo from Clients",
  private_inspo: "Private Inspo",
  master_products_linked: "Master Products Linked",
  looks_linked: "Looks Linked",
  kommo_ingest: "Kommo Ingest",
  general_inspo: "General Inspo",
};

export default function HostMediaLibraryPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFolder, setActiveFolder] = useState("");
  const [search, setSearch] = useState("");
  const [uploadModal, setUploadModal] = useState(false);
  const [uploadForm, setUploadForm] = useState({ file: null, folder: "general_inspo", name: "", tags: "" });
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("session_token");
      const qs = new URLSearchParams();
      if (activeFolder) qs.set("folder", activeFolder);
      if (search) qs.set("q", search);
      const res = await fetch(`${API}/api/host/media?${qs}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setItems(data.items || []);
      setFolders(data.folders || []);
    } catch (err) {
      toast.error("Could not load media: " + err.message);
    } finally {
      setLoading(false);
    }
  }, [activeFolder, search]);

  useEffect(() => { load(); }, [load]);

  const counts = folders.reduce((acc, f) => {
    acc[f] = items.filter((i) => i.folder === f).length;
    return acc;
  }, {});

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadForm.file) {
      toast.error("Select a file first");
      return;
    }
    setUploading(true);
    try {
      const token = localStorage.getItem("session_token");
      // Step 1: upload to Cloudinary via existing endpoint
      const fd = new FormData();
      fd.append("file", uploadForm.file);
      fd.append("folder", `phygital/hosts/${uploadForm.folder}`);
      const up = await fetch(`${API}/api/cloudinary/upload-image`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!up.ok) throw new Error("Cloudinary upload failed");
      const upData = await up.json();
      // Step 2: register in host_media
      const tags = uploadForm.tags.split(",").map((t) => t.trim()).filter(Boolean);
      const res = await fetch(`${API}/api/host/media`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          name: uploadForm.name || uploadForm.file.name,
          image_url: upData.url || upData.secure_url,
          cloudinary_public_id: upData.public_id,
          folder: uploadForm.folder,
          tags_host_visible: tags,
          source: "upload",
        }),
      });
      if (!res.ok) throw new Error(await res.text());
      toast.success("Uploaded");
      setUploadModal(false);
      setUploadForm({ file: null, folder: "general_inspo", name: "", tags: "" });
      load();
    } catch (err) {
      toast.error(String(err.message || err));
    } finally {
      setUploading(false);
    }
  };

  const deleteItem = async (media_id) => {
    if (!window.confirm("Delete this media?")) return;
    const token = localStorage.getItem("session_token");
    try {
      const res = await fetch(`${API}/api/host/media/${media_id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(await res.text());
      setItems(items.filter((i) => i.media_id !== media_id));
      toast.success("Deleted");
    } catch (err) {
      toast.error(String(err.message || err));
    }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="host-media-library-page">
      <header className="bg-white border-b border-gray-200 px-4 sm:px-6 py-4 sticky top-0 z-20">
        <div className="max-w-6xl mx-auto flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Button size="sm" variant="ghost" onClick={() => navigate(-1)} data-testid="media-back-btn">
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <FolderOpen className="w-5 h-5 text-indigo-600" />
            <div>
              <h1 className="text-lg font-semibold">Media Library</h1>
              <p className="text-xs text-gray-500">
                {items.length} items across {folders.length} folders
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={load}
              className="p-2 rounded-lg hover:bg-gray-100"
              data-testid="media-refresh-btn"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
            <Button
              size="sm"
              onClick={() => setUploadModal(true)}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="media-upload-btn"
            >
              <Upload className="w-4 h-4 mr-1" /> Upload
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 grid grid-cols-1 md:grid-cols-[200px_1fr] gap-5">
        {/* Folder sidebar */}
        <aside className="space-y-1">
          <button
            className={`w-full text-left px-3 py-2 rounded-lg text-sm ${!activeFolder ? "bg-indigo-50 text-indigo-700 font-medium" : "hover:bg-gray-100"}`}
            onClick={() => setActiveFolder("")}
            data-testid="media-folder-all"
          >
            <span className="flex items-center justify-between">
              <span>All media</span>
              <span className="text-xs">{items.length}</span>
            </span>
          </button>
          {folders.map((f) => (
            <button
              key={f}
              className={`w-full text-left px-3 py-2 rounded-lg text-sm ${activeFolder === f ? "bg-indigo-50 text-indigo-700 font-medium" : "hover:bg-gray-100"}`}
              onClick={() => setActiveFolder(f)}
              data-testid={`media-folder-${f}`}
            >
              <span className="flex items-center justify-between">
                <span>{FOLDER_LABELS[f] || f}</span>
                <span className="text-xs">{counts[f] || 0}</span>
              </span>
            </button>
          ))}
        </aside>

        {/* Main content */}
        <section>
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or tag..."
              className="pl-9"
              data-testid="media-search-input"
            />
          </div>

          {loading ? (
            <div className="text-center py-20 text-gray-500">
              <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
              Loading...
            </div>
          ) : items.length === 0 ? (
            <div className="bg-white rounded-xl p-12 text-center border border-dashed border-gray-300" data-testid="media-empty">
              <ImageIcon className="w-10 h-10 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-500">No media in this folder yet.</p>
              <Button
                onClick={() => setUploadModal(true)}
                className="mt-3 bg-indigo-600 hover:bg-indigo-700 text-white"
                size="sm"
              >
                <Upload className="w-4 h-4 mr-1" /> Upload first asset
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {items.map((it) => (
                <div
                  key={it.media_id}
                  className="group bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-md transition"
                  data-testid={`media-item-${it.media_id}`}
                >
                  <div className="aspect-square bg-gray-100 relative">
                    {it.image_url ? (
                      <img
                        src={it.image_url}
                        alt={it.name}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-300">
                        <ImageIcon className="w-8 h-8" />
                      </div>
                    )}
                    {it.is_private && (
                      <div className="absolute top-1.5 right-1.5 bg-black/60 backdrop-blur text-white rounded-full p-1">
                        <EyeOff className="w-3 h-3" />
                      </div>
                    )}
                    <button
                      onClick={() => deleteItem(it.media_id)}
                      className="opacity-0 group-hover:opacity-100 absolute top-1.5 left-1.5 bg-red-500 text-white rounded-full p-1 transition"
                      data-testid={`media-delete-${it.media_id}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                  <div className="p-2">
                    <p className="text-xs font-medium truncate">{it.name || "(untitled)"}</p>
                    <p className="text-[10px] text-gray-500 truncate">{FOLDER_LABELS[it.folder] || it.folder}</p>
                    {it.tags_host_visible?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {it.tags_host_visible.slice(0, 2).map((t) => (
                          <span key={t} className="text-[9px] bg-gray-100 rounded px-1 py-0.5">{t}</span>
                        ))}
                      </div>
                    )}
                    {it.master_product && (
                      <p className="flex items-center gap-1 text-[10px] text-indigo-600 mt-1">
                        <Link2 className="w-3 h-3" /> {it.master_product.name}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>

      {/* Upload modal */}
      {uploadModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="upload-modal">
          <div className="bg-white rounded-xl p-5 w-full max-w-md">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <Upload className="w-4 h-4" /> Upload Media
            </h3>
            <form onSubmit={handleUpload} className="space-y-3">
              <div>
                <label className="text-xs font-medium text-gray-700">File</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setUploadForm({ ...uploadForm, file: e.target.files[0] })}
                  className="w-full mt-1 text-sm"
                  required
                  data-testid="upload-file-input"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-700">Folder</label>
                <select
                  value={uploadForm.folder}
                  onChange={(e) => setUploadForm({ ...uploadForm, folder: e.target.value })}
                  className="w-full mt-1 px-3 py-2 text-sm border border-gray-200 rounded-lg"
                  data-testid="upload-folder-select"
                >
                  {folders.map((f) => <option key={f} value={f}>{FOLDER_LABELS[f] || f}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-700">Name (optional)</label>
                <Input
                  value={uploadForm.name}
                  onChange={(e) => setUploadForm({ ...uploadForm, name: e.target.value })}
                  className="mt-1 text-sm"
                  data-testid="upload-name-input"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-700 flex items-center gap-1">
                  <Tag className="w-3 h-3" /> Tags (comma-separated, host-visible)
                </label>
                <Input
                  value={uploadForm.tags}
                  onChange={(e) => setUploadForm({ ...uploadForm, tags: e.target.value })}
                  placeholder="bridal, natural, editorial"
                  className="mt-1 text-sm"
                  data-testid="upload-tags-input"
                />
              </div>
              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={() => setUploadModal(false)} className="flex-1">
                  Cancel
                </Button>
                <Button type="submit" disabled={uploading} className="flex-1" data-testid="upload-submit-btn">
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Upload"}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
